import React, { useState, useEffect } from 'react';
import { useFirebase } from '../contexts/FirebaseContext';
import { 
  getDisputes, 
  getDisputesByEmail, 
  Dispute, 
  resolveSupportInquiry, 
  replyToSupportInquiry,
  submitNewDisputeRequest
} from '../services/disputeService';
import { isSupportInquiry, getDisputeReasonInfo, formatDisputeReason } from '../lib/disputeUtils';
import { 
  HelpCircle, 
  MessageSquare, 
  CheckCircle2, 
  Clock, 
  Search, 
  Filter, 
  Send, 
  ArrowUpRight, 
  AlertCircle, 
  Sparkles, 
  Calendar, 
  User, 
  DollarSign, 
  Check, 
  X,
  RefreshCw,
  Plus
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { DisputeEntryModal } from './DisputeEntryModal';

interface Props {
  tenantId: string;
  isAdminOrManager: boolean;
  onOpenFinancialDispute?: (prefill?: any) => void;
}

export const SupportInquiries: React.FC<Props> = ({ 
  tenantId, 
  isAdminOrManager,
  onOpenFinancialDispute 
}) => {
  const { user } = useFirebase();
  const [inquiries, setInquiries] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'Pending' | 'Resolved'>('ALL');
  const [selectedInquiry, setSelectedInquiry] = useState<Dispute | null>(null);

  // Reply Modal State
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);

  // Resolve Modal State
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');
  const [isResolving, setIsResolving] = useState(false);

  // New Inquiry Modal State
  const [newInquiryOpen, setNewInquiryOpen] = useState(false);

  const fetchInquiries = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      let rawDisputes: Dispute[] = [];
      if (isAdminOrManager) {
        rawDisputes = await getDisputes(tenantId);
      } else if (user?.email) {
        rawDisputes = await getDisputesByEmail(tenantId, user.email);
      }
      
      // Filter strictly for Support / Benchmark / Informational Inquiries
      const filtered = rawDisputes.filter(d => isSupportInquiry(d));
      
      // Sort newest first
      filtered.sort((a, b) => {
        const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0);
        const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0);
        return timeB - timeA;
      });

      setInquiries(filtered);
    } catch (err) {
      console.error('Failed to load support inquiries:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, [tenantId, isAdminOrManager, user?.email]);

  const handleSendReply = async () => {
    if (!selectedInquiry?.id || !user?.email || !replyText.trim()) return;
    setIsSendingReply(true);
    try {
      await replyToSupportInquiry(
        tenantId,
        selectedInquiry.id,
        selectedInquiry,
        user.email,
        replyText.trim()
      );
      setReplyModalOpen(false);
      setReplyText('');
      await fetchInquiries();
    } catch (err) {
      console.error('Error sending reply:', err);
    } finally {
      setIsSendingReply(false);
    }
  };

  const handleResolve = async (status: 'Resolved' | 'Rejected' = 'Resolved') => {
    if (!selectedInquiry?.id || !user?.email) return;
    setIsResolving(true);
    try {
      await resolveSupportInquiry(
        tenantId,
        selectedInquiry.id,
        selectedInquiry,
        user.email,
        resolveNotes.trim() || (status === 'Resolved' ? 'Inquiry answered and verified with employee.' : 'Inquiry reviewed and closed.'),
        status
      );
      setResolveModalOpen(false);
      setResolveNotes('');
      await fetchInquiries();
    } catch (err) {
      console.error('Error resolving inquiry:', err);
    } finally {
      setIsResolving(false);
    }
  };

  // Convert inquiry to a structured financial tip dispute
  const handleConvertToFinancial = (inquiry: Dispute) => {
    if (onOpenFinancialDispute) {
      onOpenFinancialDispute({
        staffName: inquiry.staffName,
        email: inquiry.email,
        tipDate: inquiry.tipDate,
        shift: inquiry.shift,
        reason: inquiry.reason,
        explanation: inquiry.explanation,
        masterTxId: inquiry.masterTxId
      });
    }
  };

  const filteredInquiries = inquiries.filter(item => {
    if (statusFilter !== 'ALL' && item.status !== statusFilter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const staffName = (item.staffName || '').toLowerCase();
      const email = (item.email || '').toLowerCase();
      const reason = (item.reason || '').toLowerCase();
      const explanation = (item.explanation || '').toLowerCase();
      const label = formatDisputeReason(item.reason).toLowerCase();
      return staffName.includes(q) || email.includes(q) || reason.includes(q) || explanation.includes(q) || label.includes(q);
    }
    return true;
  });

  const pendingCount = inquiries.filter(i => i.status === 'Pending').length;
  const resolvedCount = inquiries.filter(i => i.status === 'Resolved').length;

  return (
    <div className="space-y-6">
      {/* Header & Overview Banner */}
      <div className="p-6 bg-gradient-to-r from-blue-50/80 via-indigo-50/50 to-white border border-blue-100 rounded-3xl flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-600 text-white rounded-xl shadow-md shadow-blue-500/20">
              <HelpCircle size={20} />
            </div>
            <h2 className="text-xl font-bold text-gray-900 tracking-tight">
              {isAdminOrManager ? 'Support & Benchmark Inquiries' : 'Active Inquiries & Missing Shifts'}
            </h2>
          </div>
          <p className="text-xs text-gray-500 font-medium max-w-xl">
            {isAdminOrManager
              ? 'Track questions, benchmark queries, shift schedules, and general staff inquiries separately from formal monetary ledger adjustments.'
              : 'Report missing shift records or submit questions regarding schedules, policies, and pool allocations. Track status and manager replies in real time.'}
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={fetchInquiries}
            className="p-2.5 bg-white hover:bg-gray-50 text-gray-600 border border-gray-200 rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5"
            title="Refresh Inquiries"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Refresh</span>
          </button>

          {!isAdminOrManager && (
            <button
              onClick={() => setNewInquiryOpen(true)}
              className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-all shadow-md shadow-blue-600/20 flex items-center gap-2"
            >
              <Plus size={14} />
              Submit Missing Shift / Inquiry
            </button>
          )}
        </div>
      </div>

      {/* Filter and Stats Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Status Filters */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setStatusFilter('ALL')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${
              statusFilter === 'ALL'
                ? 'bg-gray-900 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All ({inquiries.length})
          </button>
          <button
            onClick={() => setStatusFilter('Pending')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
              statusFilter === 'Pending'
                ? 'bg-amber-500 text-white shadow-sm'
                : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200/60'
            }`}
          >
            <Clock size={13} />
            Pending ({pendingCount})
          </button>
          <button
            onClick={() => setStatusFilter('Resolved')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
              statusFilter === 'Resolved'
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200/60'
            }`}
          >
            <CheckCircle2 size={13} />
            Resolved ({resolvedCount})
          </button>
        </div>

        {/* Search Field */}
        <div className="relative max-w-xs w-full">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search inquiries or staff..."
            className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm"
          />
        </div>
      </div>

      {/* Main Inquiry Feed */}
      {loading ? (
        <div className="p-12 text-center bg-white border border-gray-100 rounded-3xl shadow-sm space-y-3">
          <RefreshCw size={24} className="animate-spin text-blue-600 mx-auto" />
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Loading support inquiries...</p>
        </div>
      ) : filteredInquiries.length === 0 ? (
        <div className="p-12 text-center bg-white border border-gray-100 rounded-3xl shadow-sm space-y-3">
          <div className="w-12 h-12 bg-gray-50 text-gray-400 rounded-2xl flex items-center justify-center mx-auto">
            <CheckCircle2 size={24} className="text-emerald-500" />
          </div>
          <h3 className="text-base font-bold text-gray-900">No Support Inquiries</h3>
          <p className="text-xs text-gray-400 max-w-sm mx-auto">
            {statusFilter === 'Pending' 
              ? 'All staff questions, benchmark inquiries, and schedule inquiries are up to date!' 
              : 'No inquiries found matching your filters.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredInquiries.map((inquiry) => {
            const reasonInfo = getDisputeReasonInfo(inquiry.reason);
            const isPending = inquiry.status === 'Pending';
            const formattedDate = inquiry.createdAt?.toDate 
              ? inquiry.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
              : (inquiry.createdAt?.seconds ? new Date(inquiry.createdAt.seconds * 1000).toLocaleDateString() : 'Recent');

            return (
              <div 
                key={inquiry.id}
                className={`p-6 bg-white rounded-3xl border transition-all duration-200 shadow-sm hover:shadow-md ${
                  isPending ? 'border-amber-200/80 bg-amber-50/10' : 'border-gray-100'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  {/* Inquiry Main Details */}
                  <div className="space-y-3 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border ${
                        reasonInfo.badgeColor || 'bg-blue-50 text-blue-700 border-blue-200'
                      }`}>
                        {reasonInfo.label}
                      </span>

                      <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-1 ${
                        isPending ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
                      }`}>
                        {isPending ? <Clock size={11} /> : <Check size={11} />}
                        {inquiry.status}
                      </span>

                      <span className="text-xs text-gray-400 font-medium">
                        Submitted: {formattedDate}
                      </span>
                    </div>

                    <div>
                      <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                        <span>{inquiry.staffName || 'Team Member'}</span>
                        {inquiry.email && (
                          <span className="text-xs font-normal text-gray-400 font-mono">({inquiry.email})</span>
                        )}
                      </h3>
                      {inquiry.tipDate && (
                        <p className="text-xs text-gray-500 font-medium flex items-center gap-1.5 mt-0.5">
                          <Calendar size={13} className="text-gray-400" />
                          <span>Shift Date: <strong>{inquiry.tipDate}</strong></span>
                          {inquiry.shift && <span>• Shift: <strong>{inquiry.shift}</strong></span>}
                        </p>
                      )}
                    </div>

                    {/* Employee Question / Notes */}
                    <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 text-xs text-gray-700 leading-relaxed">
                      <p className="font-bold text-[10px] uppercase tracking-wider text-gray-400 mb-1">
                        Inquiry Details / Employee Notes:
                      </p>
                      <p className="font-medium text-gray-800 italic">
                        "{inquiry.explanation || inquiry.reason || 'General inquiry regarding shift tips/benchmarks.'}"
                      </p>
                    </div>

                    {/* Manager's Previous Response if already sent */}
                    {inquiry.managerComment && (
                      <div className="p-4 bg-blue-50/80 rounded-2xl border border-blue-100 text-xs text-blue-900 leading-relaxed">
                        <p className="font-bold text-[10px] uppercase tracking-wider text-blue-600 mb-1 flex items-center gap-1">
                          <MessageSquare size={12} />
                          Manager Response / Notes:
                        </p>
                        <p className="font-medium text-blue-950">
                          {inquiry.managerComment}
                        </p>
                        {inquiry.resolvedBy && (
                          <p className="text-[10px] text-blue-500 font-mono mt-1">
                            By: {inquiry.resolvedBy}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action Controls for Management */}
                  {isAdminOrManager && (
                    <div className="flex flex-row lg:flex-col items-center lg:items-end gap-2 shrink-0 border-t lg:border-t-0 pt-3 lg:pt-0">
                      {isPending ? (
                        <>
                          <button
                            onClick={() => {
                              setSelectedInquiry(inquiry);
                              setReplyModalOpen(true);
                            }}
                            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 shadow-sm"
                          >
                            <MessageSquare size={13} />
                            Reply to Staff
                          </button>

                          <button
                            onClick={() => {
                              setSelectedInquiry(inquiry);
                              setResolveModalOpen(true);
                            }}
                            className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 shadow-sm"
                          >
                            <CheckCircle2 size={13} />
                            Mark Resolved
                          </button>

                          {onOpenFinancialDispute && (
                            <button
                              onClick={() => handleConvertToFinancial(inquiry)}
                              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition-all flex items-center gap-1"
                              title="If money was actually missed, create a financial tip dispute"
                            >
                              <DollarSign size={13} className="text-emerald-600" />
                              <span className="text-[11px]">Convert to Tip Dispute</span>
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedInquiry(inquiry);
                              setReplyModalOpen(true);
                            }}
                            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5"
                          >
                            <MessageSquare size={13} />
                            Send Another Message
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reply Modal */}
      <AnimatePresence>
        {replyModalOpen && selectedInquiry && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-xs">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl max-w-lg w-full p-6 space-y-4 shadow-2xl border border-gray-100"
            >
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                    <MessageSquare size={18} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">Reply to Employee</h3>
                    <p className="text-xs text-gray-400 font-medium">To: {selectedInquiry.staffName} ({selectedInquiry.email})</p>
                  </div>
                </div>
                <button 
                  onClick={() => setReplyModalOpen(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded-lg"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="p-3 bg-gray-50 rounded-2xl border border-gray-100 text-xs text-gray-600">
                <span className="font-bold text-gray-700">Regarding: </span>
                {formatDisputeReason(selectedInquiry.reason)} 
                {selectedInquiry.explanation && ` - "${selectedInquiry.explanation}"`}
              </div>

              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-1.5">
                  Your Message (Sent via In-App Notification & Email)
                </label>
                <textarea
                  rows={4}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="e.g., I checked your shift records and confirmed the POS clock-in was recorded properly..."
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  onClick={() => setReplyModalOpen(false)}
                  className="px-4 py-2.5 text-xs font-bold text-gray-500 hover:text-gray-700 rounded-xl"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendReply}
                  disabled={isSendingReply || !replyText.trim()}
                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 shadow-md shadow-blue-600/20"
                >
                  <Send size={13} />
                  {isSendingReply ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Resolve Modal */}
      <AnimatePresence>
        {resolveModalOpen && selectedInquiry && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-xs">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl max-w-lg w-full p-6 space-y-4 shadow-2xl border border-gray-100"
            >
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl">
                    <CheckCircle2 size={18} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">Resolve Inquiry</h3>
                    <p className="text-xs text-gray-400 font-medium">Clear from pending queue without creating monetary ledger adjustments</p>
                  </div>
                </div>
                <button 
                  onClick={() => setResolveModalOpen(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded-lg"
                >
                  <X size={18} />
                </button>
              </div>

              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-1.5">
                  Resolution Notes (Shared with Employee)
                </label>
                <textarea
                  rows={3}
                  value={resolveNotes}
                  onChange={(e) => setResolveNotes(e.target.value)}
                  placeholder="e.g., Verified shift schedule and explained benchmark calculation directly to employee."
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <button
                  onClick={() => handleResolve('Rejected')}
                  disabled={isResolving}
                  className="px-3 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50 rounded-xl"
                >
                  Dismiss / Reject
                </button>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setResolveModalOpen(false)}
                    className="px-4 py-2.5 text-xs font-bold text-gray-500 hover:text-gray-700 rounded-xl"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleResolve('Resolved')}
                    disabled={isResolving}
                    className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 shadow-md shadow-emerald-600/20"
                  >
                    <CheckCircle2 size={13} />
                    {isResolving ? 'Resolving...' : 'Confirm Resolution'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* New Inquiry Modal for Employee */}
      <DisputeEntryModal
        isOpen={newInquiryOpen}
        onClose={() => {
          setNewInquiryOpen(false);
          fetchInquiries();
        }}
        tenantId={tenantId}
        employeeId={user?.uid || ''}
        email={user?.email || ''}
        context={{}}
      />
    </div>
  );
};
